import type { RoomMembership } from '@brush/api-client';

/**
 * How a card came to be connected, in an operator's terms.
 *
 * Deliberately not shared with player-web's `methodLabel`, which reads the same column for a
 * different reader — that file says so from its side. A player is being told how *they* proved it;
 * an operator is being told what evidence the room holds about somebody else, which is why every
 * entry here carries a second sentence and none of player-web's do. Same rule that keeps
 * `LeaderboardBreakdown` in that app rather than in `packages/ui`.
 */
export function methodEvidence(method: string): { label: string; gloss: string } {
  switch (method) {
    case 'platform_otp_email':
      return {
        label: 'Emailed code',
        gloss: 'They proved they can read the email address this room has on the card.',
      };
    case 'platform_otp_sms':
      return {
        label: 'Texted code',
        gloss: 'They proved they can read the phone number this room has on the card.',
      };
    case 'room_idp_attested':
      return {
        label: 'Room sign-in vouched',
        gloss: "This room's own identity provider was asked mid-claim and vouched for them.",
      };
    case 'room_idp_federated':
      return {
        label: 'Signed in through this room',
        gloss: "They signed in with this room's own identity provider rather than the platform's.",
      };
    case 'room_api':
      return {
        label: "This room's records",
        gloss: "This room's own system answered a lookup and matched them to the card.",
      };
    case 'verified_email_match':
      return {
        label: 'Verified address matched',
        gloss:
          'No code was sent. Their sign-in carried an address the provider had already verified, ' +
          'and it equalled the one on the card.',
      };
    default:
      // A method this build does not know about is still a real membership, and the honest answer
      // is that we cannot say what was proven — never a guess and never the raw literal. Adding a
      // value to the server's vocabulary needs no migration, so this branch is reached by a deploy
      // order rather than by a bug.
      return {
        label: 'Connected',
        gloss: 'This build does not recognise how this one was proved.',
      };
  }
}

/**
 * A contact that changed shortly before the card was claimed, said plainly — or null.
 *
 * **This is the visible half of ADR 0010 Decision 8's control, and the wording is the control.**
 * With no operator link path, editing a card's contact is the only lever a room has over who can
 * claim it, and an operator who can set an address can set it to one they hold. That is not
 * blocked, deliberately: a desk correcting an address while the player waits is the ordinary case
 * and has to stay fast. So it is made *legible* instead — and it is neither a warning nor a
 * judgement, because the honest case and the other one produce identical rows and a screen that
 * pretended to tell them apart would be wrong about half of them.
 *
 * A day, rather than the minutes the ADR's example uses. Shorter and the line stops appearing for
 * an edit made at the start of a shift for a player who claimed at the end of it; much longer and
 * every row carries it, which is the same as no row carrying it.
 */
export function contactChangeNote(m: RoomMembership): string | null {
  if (!m.contactUpdatedAt) return null;

  const gap = Date.parse(m.linkedAt) - Date.parse(m.contactUpdatedAt);
  if (gap < 0 || gap > DAY) return null;

  const who = m.contactUpdatedByName ?? 'somebody at this room';
  return `Connected ${describeGap(gap)} after this card's contact was changed by ${who}.`;
}

const DAY = 24 * 60 * 60 * 1000;

/**
 * The interval in the unit a person would say it in — **truncated, and matching
 * `MembershipService.ContactAge` exactly**, thresholds included.
 *
 * That agreement is the whole point of this function rather than a nicety. The same interval is
 * stated twice on one card, a line apart: the stored evidence line says `contact 2h old at send`
 * and this sentence says `Connected N after…`. Rounding here while the server truncated turned one
 * interval of about 2h55m into "2h" above "3 hours" — and an operator reading the audit line
 * carefully, which is the only reason it exists, is left working out which of the two to believe.
 *
 * The two still measure from slightly different moments — the server's from the send, this one
 * from the link — so a boundary case can differ by one unit. That gap is bounded by how long a
 * claimant has to type the code, and closing it would cost a field on the DTO for an error smaller
 * than the unit being displayed.
 *
 * Change either rule and change both.
 */
function describeGap(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'less than a minute';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`;

  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}
